import { useQuery } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ContentfulCurrentActiveEntries, ContentfulNav } from '@jbhi-fi/apis/src/contentful/types';
import { useContentfulCurrentWebsiteElements } from '@jbhi-fi/components/src/hooks/useContentfulCurrentWebsiteElements/useContentfulCurrentWebsiteElements';
import { ContentfulClient } from '@jbhi-fi/apis/src/contentful/ContentfulClient';

export type UseContentfulActiveNavsProps = {
  contentfulThemeApiClient?: ContentfulThemeApiClient;
  contentfulClient?: ContentfulClient;
};

type CurrentActiveNavs = Pick<
  Exclude<ContentfulCurrentActiveEntries['fields'], undefined>,
  'headerNav' | 'footerNav' | 'footerPayments' | 'footerLegals' | 'footerSocials'
>;

export type UseContentfulActiveNavsResult = {
  isLoading?: boolean;
} & Partial<CurrentActiveNavs>;

export const useContentfulActiveNavs = ({
  contentfulThemeApiClient: contentfulThemeApiClientProp,
  contentfulClient: contentfulClientProp,
}: UseContentfulActiveNavsProps): UseContentfulActiveNavsResult => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext, contentfulClient: contentfulClientContext } =
    usePublicApiClient();
  const contentfulThemeApiClient = contentfulThemeApiClientProp || contentfulThemeApiClientContext;
  const contentfulClient = contentfulClientProp || contentfulClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error(
      'useContentfulActiveNavs must be provided with a contentfulThemeApiClient via PublicApiClientProvider or props'
    );
  }
  const { isLoading: isActiveEntryLoading, currentWebsiteEntryIds } = useContentfulCurrentWebsiteElements();

  const shouldUseCachedHeaderFooter = window?.Jbhifi?.contentfulSettings?.shouldUseCachedHeaderFooter ?? true;

  const { headerNav } = currentWebsiteEntryIds ?? {};

  const navEntriesIds = [...(!shouldUseCachedHeaderFooter ? [headerNav] : [])].filter(Boolean);
  const isPreview = window.Jbhifi?.isContentPreview || false;

  const { isLoading: isNavEntriesLoading, data: navEntriesData } = useQuery({
    enabled: !!(contentfulThemeApiClient && navEntriesIds.length),
    queryKey: ['useContentfulActiveNavs', 'navEntries', navEntriesIds],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const baseQueryOptions = {
        content_type: 'nav',
        include: '6',
        'sys.id[in]': navEntriesIds.join(','),
      };
      const shouldUseThemeApi = isPreview || !contentfulClient;
      const queryOptions = shouldUseThemeApi ? { ...baseQueryOptions, cache: 'medium' } : baseQueryOptions;

      return shouldUseThemeApi
        ? await contentfulThemeApiClient.getEntries(queryOptions)
        : await contentfulClient.getEntries(queryOptions);
    },
  });

  const items = (navEntriesData?.data?.items ?? []) as Array<ContentfulNav>;

  return {
    isLoading: isActiveEntryLoading || isNavEntriesLoading,
    headerNav: items?.find(item => item.fields?.type === 'Header Nav'),
  };
};
